// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — reusable dashboard shell components.
//
// KpiCard, ChartCard, AnalyticsFilters, DrilldownDrawer, EmptyMetricState,
// MetricUnavailableState + the useDrilldown hook. These compose the six lenses;
// none of them know about a specific metric — they're generic building blocks.
import React, { useState, useCallback, useRef } from "react";
import { apiFetch, fmt } from "../api";
import { Spinner, EmptyState } from "../components/ui";

// ---- KPI card (clickable, optional sparkline) -----------------------------
export function KpiCard({ label, value, sub, tone, onClick, spark }) {
  return (
    <div className={`an-kpi${tone ? " " + tone : ""}${onClick ? " clickable" : ""}`}
         onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className="an-kpi-label">{label}</div>
      <div className="an-kpi-value">{value}</div>
      {sub != null && <div className="an-kpi-sub">{sub}</div>}
      {spark && spark.length > 1 && <Sparkline data={spark} />}
    </div>
  );
}

function Sparkline({ data, color = "#3b6fd4", w = 120, h = 22 }) {
  const vals = data.map((d) => Number(d) || 0);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const span = max - min || 1;
  const pts = vals.map((v, i) =>
    `${(i / (vals.length - 1)) * w},${h - ((v - min) / span) * h}`).join(" ");
  return (
    <svg className="an-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

// ---- Chart card wrapper (handles loading / empty / unavailable) -----------
export function ChartCard({ title, subtitle, controls, loading, error, empty, unavailable,
                            wide, children, footer, height }) {
  return (
    <div className={`an-card${wide ? " wide" : ""}`}>
      <div className="an-card-head">
        <div>
          <div className="an-card-title">{title}</div>
          {subtitle && <div className="an-card-sub">{subtitle}</div>}
        </div>
        {controls && <div className="an-card-controls">{controls}</div>}
      </div>
      <div className="an-card-body" style={height ? { minHeight: height } : undefined}>
        {unavailable ? <MetricUnavailableState {...unavailable} />
          : loading ? <Spinner />
          : error ? <EmptyMetricState icon="⚠️" title="Couldn't load" hint={String(error)} />
          : empty ? <EmptyMetricState icon="📭" title="No data" hint="Nothing matches the current filters." />
          : children}
      </div>
      {footer && <div className="an-card-foot">{footer}</div>}
    </div>
  );
}

export function EmptyMetricState({ icon = "📭", title, hint }) {
  return <EmptyState icon={icon} title={title} hint={hint} />;
}

// MetricUnavailableState — disabled/dependency card. `status` tags the reason:
// "Deferred" | "Needs Source Data" | "Coming Soon" | "Partial".
export function MetricUnavailableState({ reason, status = "Not Available", dependency }) {
  const tone = status === "Partial" ? "amber" : "slate";
  return (
    <div className="an-unavailable">
      <div className="an-unavailable-icon">{status === "Partial" ? "◐" : "🔒"}</div>
      <div className={`pill pill-${tone} pill-sm`}>{status}</div>
      <div className="an-unavailable-reason">{reason}</div>
      {dependency && <div className="an-unavailable-dep">Depends on: {dependency}</div>}
    </div>
  );
}

// ---- Global filter bar ----------------------------------------------------
const STATIC = {
  business_line: ["P&I", "H&M", "Other"],
  claim_type: [
    ["pi_crew", "P&I Crew"], ["pi_passenger", "P&I Passenger"], ["pi_other", "P&I Other"],
    ["uslh", "USL&H"], ["hull_machinery", "Hull & Machinery"], ["pollution", "Pollution"],
    ["cargo", "Cargo"], ["other", "Other"],
  ],
  status: ["Open", "New Claim", "Closed"],
  matter_type: [["ia", "IA"], ["tpa", "TPA"]],
};
const DATE_PRESETS = [
  ["30d", "Last 30 days", 30], ["90d", "Last 90 days", 90],
  ["12m", "Last 12 months", 365], ["ytd", "Year to date", null], ["all", "All time", null],
];

export function AnalyticsFilters({ value, onChange, adjusters, refreshedAt, onRefresh }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const applyPreset = (key) => {
    const today = new Date();
    let from = null;
    if (key === "ytd") from = `${today.getFullYear()}-01-01`;
    else if (key === "all") from = null;
    else {
      const days = DATE_PRESETS.find((p) => p[0] === key)[2];
      const d = new Date(); d.setDate(d.getDate() - days);
      from = d.toISOString().slice(0, 10);
    }
    set({ preset: key, date_from: from, date_to: today.toISOString().slice(0, 10) });
  };
  const reset = () => onChange({ preset: "90d", date_from: presetFrom("90d"), date_to: todayISO() });

  return (
    <div className="an-filters">
      <div className="an-filter-group">
        {DATE_PRESETS.map(([k, lbl]) => (
          <button key={k} className={`an-chip${value.preset === k ? " active" : ""}`}
                  onClick={() => applyPreset(k)}>{lbl}</button>
        ))}
      </div>
      <Sel label="Business line" v={value.business_line} onV={(x) => set({ business_line: x })}
           opts={STATIC.business_line.map((b) => [b, b])} />
      <Sel label="Claim type" v={value.claim_type} onV={(x) => set({ claim_type: x })}
           opts={STATIC.claim_type} />
      <Sel label="Status" v={value.status} onV={(x) => set({ status: x })}
           opts={STATIC.status.map((s) => [s, s])} />
      <Sel label="Matter" v={value.matter_type} onV={(x) => set({ matter_type: x })}
           opts={STATIC.matter_type} />
      {adjusters && adjusters.length > 0 && (
        <Sel label="Adjuster" v={value.adjuster_id} onV={(x) => set({ adjuster_id: x })}
             opts={adjusters.map((a) => [String(a.id), `${a.full_name} (${a.claim_count})`])} />
      )}
      <button className="an-reset" onClick={reset}>Reset</button>
      <div className="an-refreshed">
        {refreshedAt && <span>Updated {fmt.datetime(refreshedAt)}</span>}
        {onRefresh && <button className="an-refresh-btn" onClick={onRefresh} title="Refresh">⟳</button>}
      </div>
    </div>
  );
}

function Sel({ label, v, onV, opts }) {
  return (
    <label className="an-sel">
      <span>{label}</span>
      <select value={v || ""} onChange={(e) => onV(e.target.value || null)}>
        <option value="">All</option>
        {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </label>
  );
}

export function todayISO() { return new Date().toISOString().slice(0, 10); }
export function presetFrom(key) {
  const days = { "30d": 30, "90d": 90, "12m": 365 }[key];
  if (!days) return null;
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// ---- Drilldown drawer + hook ----------------------------------------------
export function useDrilldown(filters) {
  const [state, setState] = useState({ open: false, title: "", metric: null, value: null,
                                        loading: false, error: null, data: null });
  const reqId = useRef(0);

  const open = useCallback((metric, value, title) => {
    const id = ++reqId.current;
    setState({ open: true, title, metric, value, loading: true, error: null, data: null });
    const qs = buildQS({ ...filters, metric, value, page_size: 200 });
    apiFetch(`/analytics/drilldown?${qs}`)
      .then((d) => { if (id === reqId.current) setState((s) => ({ ...s, loading: false, data: d })); })
      .catch((e) => { if (id === reqId.current) setState((s) => ({ ...s, loading: false, error: e.message || String(e) })); });
  }, [filters]);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return { drill: state, openDrill: open, closeDrill: close };
}

export function buildQS(obj) {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => { if (v != null && v !== "") p.append(k, v); });
  return p.toString();
}

export function DrilldownDrawer({ drill, onClose, onOpenClaim }) {
  if (!drill.open) return null;
  const d = drill.data;
  const rows = (d && d.rows) || [];
  const exportCsv = () => {
    const cols = ["claim_number", "insured", "claimant", "status", "lead_adjuster",
                  "date_of_loss", "total_reserve", "total_paid", "remaining"];
    const head = cols.join(",");
    const body = rows.map((r) => cols.map((c) => csv(r[c])).join(",")).join("\n");
    downloadText(`${drill.metric}-drilldown.csv`, head + "\n" + body);
  };
  return (
    <div className="an-drawer-overlay" onClick={onClose}>
      <div className="an-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="an-drawer-head">
          <div>
            <div className="an-drawer-title">{drill.title}</div>
            {d && <div className="an-drawer-meta">{d.total} claim{d.total === 1 ? "" : "s"}
              {d.total > rows.length ? ` — showing first ${rows.length}` : ""}</div>}
          </div>
          <div className="an-drawer-actions">
            {rows.length > 0 && <button className="an-btn" onClick={exportCsv}>⤓ CSV</button>}
            <button className="an-btn an-btn-close" onClick={onClose}>×</button>
          </div>
        </div>
        {d && <ContextChips ctx={d.filter_context} />}
        <div className="an-drawer-body">
          {drill.loading ? <Spinner />
            : drill.error ? <EmptyMetricState icon="⚠️" title="Couldn't load" hint={drill.error} />
            : rows.length === 0 ? <EmptyMetricState title="No matching claims" />
            : (
              <table className="an-drill-table">
                <thead>
                  <tr>
                    <th>Claim #</th><th>Insured</th><th>Claimant</th><th>Status</th>
                    <th>Adjuster</th><th className="r">Reserve</th><th className="r">Paid</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.claim_id}>
                      <td className="mono">{r.claim_number}</td>
                      <td>{r.insured || "—"}</td>
                      <td>{r.claimant || "—"}</td>
                      <td><span className="pill pill-sm pill-slate">{r.status}</span></td>
                      <td>{r.lead_adjuster || "—"}</td>
                      <td className="r num">{fmt.money(r.total_reserve)}</td>
                      <td className="r num">{fmt.money(r.total_paid)}</td>
                      <td><button className="an-link" onClick={() => onOpenClaim && onOpenClaim(r.claim_id)}>Open ↗</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

function ContextChips({ ctx }) {
  if (!ctx) return null;
  const chips = [];
  if (ctx.date_from) chips.push(`${ctx.date_from} → ${ctx.date_to}`);
  if (ctx.business_line) chips.push(`Line: ${ctx.business_line}`);
  if (ctx.claim_type) chips.push(`Type: ${ctx.claim_type}`);
  if (ctx.status) chips.push(`Status: ${ctx.status}`);
  if (ctx.matter_type) chips.push(`Matter: ${ctx.matter_type.toUpperCase()}`);
  if (!chips.length) return null;
  return <div className="an-context">{chips.map((c, i) => <span key={i} className="an-context-chip">{c}</span>)}</div>;
}

function csv(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadText(name, text) {
  const blob = new Blob([text], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
